// frontend/src/marketing/pages/legal/PrivacyPage.tsx
import * as React from 'react';
import { LegalPageLayout, type LegalSection } from './LegalPageLayout';

const SECTIONS: LegalSection[] = [
  {
    heading: 'Scope',
    body: (
      <p>
        This Privacy Policy explains how Worqly collects, uses and shares personal data when you
        visit worqly.io, create a workspace, or use the platform. Where your employer runs a Worqly
        workspace and you use it as an employee, your employer is the data controller for the data
        inside that workspace and this policy applies to our role as a processor — see the{' '}
        <a className="underline underline-offset-2" href="/legal/dpa">DPA</a>.
      </p>
    ),
  },
  {
    heading: 'What we collect',
    body: (
      <>
        <p>
          <strong>Account data</strong> — name, email, password hash, workspace name.{' '}
          <strong>Billing data</strong> — subscription state and invoices; card details are handled
          by Stripe and never touch our servers. <strong>Workspace content</strong> — the records
          your workspace creates (clients, invoices, employees, attendance, payroll runs, projects,
          expenses, support tickets, and — if the monitoring module is enabled by your employer —
          periodic screenshots from the desktop agent). <strong>Technical data</strong> — IP
          address, user-agent and security logs (sign-ins, sessions).
        </p>
      </>
    ),
  },
  {
    heading: 'How we use data',
    body: (
      <p>
        To operate the Service, authenticate you, send transactional email (magic links, receipts,
        payslips, support replies), prevent abuse (rate limiting, captcha), bill subscriptions, and
        improve reliability. We do not sell personal data and we do not use workspace content for
        advertising or for training AI models.
      </p>
    ),
  },
  {
    heading: 'Sub-processors',
    body: (
      <p>
        We use a small set of providers to run the Service: Contabo (server hosting — the
        application, files and database run on servers we administer there), Stripe (payments),
        Resend (transactional email), Cloudflare (Turnstile bot protection on public forms),
        Upstash (rate-limit counters), and — only if your workspace connects it — Intuit/QuickBooks
        (accounting sync). Each processes data only as needed to provide its function.
      </p>
    ),
  },
  {
    heading: 'Retention',
    body: (
      <p>
        Workspace content is retained while your workspace is active. When deletion is requested,
        the workspace enters a 30-day grace window in which it can be restored, after which it is
        purged from production systems. Database backups are kept on a rolling 14-day window, so a
        purged workspace can persist in backups until those rotate out. You can request earlier
        deletion via support.
      </p>
    ),
  },
  {
    heading: 'Your rights',
    body: (
      <p>
        Depending on your jurisdiction you may have rights to access, correct, export or delete
        your personal data. For data inside an employer-run workspace, direct requests to your
        employer (the controller); we assist them in fulfilling such requests. For account data
        where we are the controller, contact us via the{' '}
        <a className="underline underline-offset-2" href="/contact">contact form</a>.
      </p>
    ),
  },
  {
    heading: 'Security',
    body: (
      <p>
        Data is encrypted in transit (TLS) and at rest, workspaces are isolated with row-level
        security, access is role-based, and credentials are stored hashed. See the{' '}
        <a className="underline underline-offset-2" href="/security">security page</a> for the
        current posture and roadmap.
      </p>
    ),
  },
  {
    heading: 'Changes',
    body: (
      <p>
        We may update this policy; material changes are announced by email or in-app. The
        "last updated" date above always reflects the current version.
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Privacy policy"
      description="How Worqly collects, uses and protects personal data."
      updated="June 4, 2026"
      sections={SECTIONS}
    />
  );
}
